'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as Auth from '@/lib/auth';
import { cancelPendingGardenPlanSave, migrateLocalStorageToKV } from '@/lib/dataSync';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: Auth.AuthUser | null;
  loading: boolean;
  bootstrapData: Auth.UserBootstrapData | null;
  bootstrapLoading: boolean;
  login:    (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout:   () => void;
  register: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<Auth.AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapData, setBootstrapData] = useState<Auth.UserBootstrapData | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setBootstrapLoading(true);
    try {
      const bootstrap = await Auth.getBootstrap();
      setUser(bootstrap?.user ?? null);
      setBootstrapData(bootstrap?.data ?? null);
    } finally {
      setBootstrapLoading(false);
    }
  }, []);

  // Validate stored token on mount
  useEffect(() => {
    Auth.getBootstrap()
      .then(bootstrap => {
        setUser(bootstrap?.user ?? null);
        setBootstrapData(bootstrap?.data ?? null);
      })
      .finally(() => {
        setBootstrapLoading(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleAuthInvalid = () => {
      logout();
    };

    window.addEventListener('garden:auth-invalid', handleAuthInvalid);
    return () => window.removeEventListener('garden:auth-invalid', handleAuthInvalid);
  }, []);

  async function handleAuthenticatedUser(authenticatedUser: Auth.AuthUser) {
    const previousUserEmail = user?.email ?? null;
    const nextUserEmail = authenticatedUser.email;
    if (previousUserEmail && previousUserEmail !== nextUserEmail) {
      cancelPendingGardenPlanSave();
      Auth.clearStoredUserData();
    }
    setUser(authenticatedUser);
    setBootstrapData(null);
    setBootstrapLoading(true);
    await migrateLocalStorageToKV();
    try {
      const bootstrap = await Auth.getBootstrap();
      if (bootstrap) {
        setUser(bootstrap.user);
        setBootstrapData(bootstrap.data);
      }
    } finally {
      setBootstrapLoading(false);
    }
  }

  async function login(email: string, password: string) {
    const result = await Auth.login(email, password);
    if (result.success && result.user) {
      await handleAuthenticatedUser(result.user);
    }
    return { success: result.success, error: result.error };
  }

  function logout() {
    cancelPendingGardenPlanSave();
    Auth.logout();
    setUser(null);
    setBootstrapData(null);
    setBootstrapLoading(false);
  }

  async function register(email: string, password: string, name: string) {
    const result = await Auth.register(email, password, name);
    if (result.success && result.user) {
      await handleAuthenticatedUser(result.user);
    }
    return { success: result.success, error: result.error };
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      bootstrapData,
      bootstrapLoading,
      login,
      logout,
      register,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
